<script setup lang="ts">
/**
 * SyncCascade — centreret modal-cascade der trigger manuel sync af alle
 * (eller valgte) data-kilder i ét hug med live progress-display.
 *
 * Per [Fase A11.1] og forfining af [§14] (modal-dialogs er nu tilladt for
 * admin-meta-actions som sync/eksport/system-maintenance; fortsat forbudt
 * for primær-data-flow).
 *
 * Pattern:
 *   - Klik "Manuel sync" (Driftstatus-gruppen i sidebar-bunden) → cascade
 *     åbnes som overlay.
 *   - Tabel: checkbox + Kilde + Sidst synket + Denne kørsel.
 *   - Default: alle kilder valgt. Charlie unchecker dem han vil skippe.
 *   - Klik "Synk N kilder" → per-række live progress (record-count + elapsed
 *     time + spinner) via polling af /api/integrations/:provider/sync-progress
 *     hver 500ms. Sub-row viser aktuel phase + records processed + tid siden
 *     seneste update.
 *   - Under sync: footer viser "Synker… luk ikke vinduet" som advarsel.
 *   - Når alle færdige: success-banner (grøn hvis alle OK, rød hvis fejl) +
 *     Luk-knap erstatter Annullér/Synk-knapperne.
 *   - Backdrop-klik / Escape lukker cascade'n — pågående syncs afbrydes IKKE
 *     (fire-and-forget; server-side fortsætter, Charlie kan åbne igen).
 *
 * Kilde-liste er p.t. kun BC kontoplan per [Fase A11.1] — udvides efterhånden
 * som andre sync-targets bliver klar. Same syncs der senere køres som cron
 * skal også kunne trigges manuelt herfra.
 */

import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import {
  formatDateDa,
  formatDateRangeDa,
  formatDurationDa,
  formatEpochDateTimeDa,
  formatMonthDa,
  formatRelativeAgoDa,
  formatTimeDa,
} from '../lib/datetime';

interface SyncSource {
  id: string;
  /** Pænt label til UI'en, fx "BC kontoplan". */
  label: string;
  /** POST-endpoint der trigger sync. */
  syncEndpoint: string;
  /** GET-endpoint der returnerer status (last sync + this run-preview). */
  statusEndpoint?: string;
  /** Provider-id til progress-polling (/api/integrations/:provider/sync-progress). */
  progressProvider: string;
  /** Sidst synket — ISO datetime eller "Today" / "Aldrig". Null hvis aldrig. */
  lastSyncAt: string | null;
  /** "Denne kørsel"-preview: dato-range eller "Fuld backfill". */
  thisRunPreview: string;
  /** Dagen bag en 1-dags-preview ("I går"), til v-fulldate (§94). Ellers null. */
  thisRunPreviewDay?: string | null;
  /** Live-status. 'queued' = valgt, venter på sin tur i den sekventielle kaskade. */
  status: 'idle' | 'queued' | 'running' | 'success' | 'error';
  /** Detail-tekst efter sync (resultText) eller fejl-besked. */
  resultText: string | null;
  /** Om brugeren har valgt at inkludere kilden. */
  selected: boolean;
  /** Om kilden er konfigureret (credentials til stede). */
  configured: boolean;
  /** Live progress (polled). Null indtil sync starter. */
  progress: SyncProgressShape | null;
  /** Tidspunkt sync startede (ms epoch). Bruges til elapsed-time-display. */
  startedAt: number | null;
  /** Tidspunkt sync sluttede (ms epoch). Bruges til "Xs ago"-display. */
  finishedAt: number | null;
}

/** Shape som /api/integrations/:provider/sync-progress returnerer. */
interface SyncProgressShape {
  provider: string;
  phase: string;
  current: number;
  total: number | null;
  completedPhases: Record<string, number>;
  startedAt: number;
  phaseStartedAt: number;
  etaSeconds: number | null;
  isRunning: boolean;
  updatedAt: number;
}

/** Server-side kaskade-status (GET /api/integrations/sync-all/status). */
interface CascadeStepShape {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'success' | 'error';
  error: string | null;
  warnings: string[];
  startedAt: number | null;
  finishedAt: number | null;
}
interface CascadeStateShape {
  running: boolean;
  startedAt: number | null;
  finishedAt: number | null;
  steps: CascadeStepShape[];
  recompute: { status: 'pending' | 'running' | 'success' | 'error' | 'skipped'; error: string | null };
}

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

function makeSource(
  partial: Pick<SyncSource, 'id' | 'label' | 'syncEndpoint' | 'statusEndpoint' | 'progressProvider'>,
): SyncSource {
  return {
    ...partial,
    lastSyncAt: null,
    thisRunPreview: 'Indlæser…',
    status: 'idle',
    resultText: null,
    selected: true,
    configured: false,
    progress: null,
    startedAt: null,
    finishedAt: null,
  };
}

// Kilde-rækkefølge = sync-rækkefølge i UI'en (kører dog parallelt). BC først
// (tungest, leverer Real), derefter web-ordrer, trafik-kilder, ad-platforme,
// butiksbesøg, kurser og Klaviyo. Hver kilde henter frem til og med i går;
// et afsluttende recompute ([D-083]) genberegner budgettet når alle er
// færdige. Session 31-udvidelse: kaskaden dækker nu ALLE kilder (Charlie:
// "ét klik kører alle syncs") — alle forudvalgte, fravælg enkelte efter behov.
const sources = ref<SyncSource[]>([
  makeSource({
    id: 'business_central_production',
    label: 'BC kontoplan (Real)',
    syncEndpoint: '/api/integrations/business_central_production/sync',
    statusEndpoint: '/api/integrations/business_central_production/gl-summary',
    progressProvider: 'business_central_production',
  }),
  makeSource({
    id: 'woocommerce',
    label: 'WooCommerce (web-ordrer)',
    syncEndpoint: '/api/integrations/wordpress/sync',
    statusEndpoint: '/api/integrations/wordpress/woo-summary',
    progressProvider: 'wordpress',
  }),
  makeSource({
    id: 'ga4',
    label: 'GA4 (sessions)',
    syncEndpoint: '/api/integrations/google/sync?service=ga4',
    statusEndpoint: '/api/integrations/google/ga4/summary',
    progressProvider: 'google_ga4',
  }),
  makeSource({
    id: 'gsc',
    label: 'Search Console (organisk)',
    syncEndpoint: '/api/integrations/google/sync?service=gsc',
    statusEndpoint: '/api/integrations/google/gsc/summary',
    progressProvider: 'google_gsc',
  }),
  makeSource({
    id: 'gsc_queries',
    label: 'Search Console (søgeord)',
    syncEndpoint: '/api/integrations/google/sync?service=gsc_queries',
    statusEndpoint: '/api/integrations/google/gsc-queries/summary',
    progressProvider: 'google_gsc_queries',
  }),
  // Side-niveau GSC ([D-226]) — effektserien pr. URL til Webshop → SEO.
  makeSource({
    id: 'gsc_pages',
    label: 'Search Console (sider)',
    syncEndpoint: '/api/integrations/google/sync?service=gsc_pages',
    statusEndpoint: '/api/integrations/google/gsc-pages/summary',
    progressProvider: 'google_gsc_pages',
  }),
  makeSource({
    id: 'google_ads',
    label: 'Google Ads (spend/klik)',
    syncEndpoint: '/api/integrations/google/sync?service=ads',
    statusEndpoint: '/api/integrations/google/ads/summary',
    progressProvider: 'google_ads',
  }),
  makeSource({
    id: 'bing_ads',
    label: 'Bing Ads (spend/klik)',
    syncEndpoint: '/api/integrations/bing_ads/sync',
    statusEndpoint: '/api/integrations/bing_ads/summary',
    progressProvider: 'bing_ads',
  }),
  makeSource({
    id: 'meta_ads',
    label: 'Meta Ads (spend/klik)',
    syncEndpoint: '/api/integrations/meta_ads/sync',
    statusEndpoint: '/api/integrations/meta_ads/summary',
    progressProvider: 'meta_ads',
  }),
  makeSource({
    id: 'meraki',
    label: 'Meraki (butiksbesøg)',
    syncEndpoint: '/api/integrations/meraki/sync',
    statusEndpoint: '/api/integrations/meraki/summary',
    progressProvider: 'meraki',
  }),
  makeSource({
    id: 'fx',
    label: 'Valutakurser (Nationalbanken)',
    syncEndpoint: '/api/budget/sync-fx',
    statusEndpoint: '/api/budget/fx-summary',
    progressProvider: 'fx',
  }),
  makeSource({
    id: 'klaviyo',
    label: 'Klaviyo (profiler/kampagner)',
    syncEndpoint: '/api/integrations/klaviyo/sync',
    statusEndpoint: '/api/integrations/klaviyo/summary',
    progressProvider: 'klaviyo',
  }),
  makeSource({
    id: 'calendly',
    label: 'Calendly (bookinger)',
    syncEndpoint: '/api/integrations/calendly/sync',
    statusEndpoint: '/api/integrations/calendly/summary',
    progressProvider: 'calendly',
  }),
  makeSource({
    id: 'typeform',
    label: 'Typeform (form-svar)',
    syncEndpoint: '/api/integrations/typeform/sync',
    statusEndpoint: '/api/integrations/typeform/summary',
    progressProvider: 'typeform',
  }),
  makeSource({
    id: 'fluentforms',
    label: 'FluentForms (WP-henvendelser)',
    syncEndpoint: '/api/integrations/wordpress/sync?service=fluentforms',
    statusEndpoint: '/api/integrations/wordpress/fluentforms-summary',
    progressProvider: 'wordpress',
  }),
  // BC kunder ([D-155]): kartotek m. rabat-/prisgrupper — eget natjob,
  // nu også manuelt synkbar (Charlies regel: alt auto + manuelt).
  makeSource({
    id: 'bc_customers',
    label: 'BC kunder (rabat-/prisgrupper)',
    syncEndpoint: '/api/integrations/business_central_production/sync?service=customers',
    statusEndpoint: '/api/integrations/business_central_production/customers-summary',
    progressProvider: 'business_central_production',
  }),
  // SameSystem ([D-153]): vagter (seneste 35 dage — retro-justeringer).
  makeSource({
    id: 'samesystem',
    label: 'SameSystem (vagter)',
    syncEndpoint: '/api/integrations/samesystem/sync',
    statusEndpoint: '/api/integrations/samesystem/summary',
    progressProvider: 'samesystem',
  }),
  // QR TIGER ([D-161]): QR-koder + daglige scans. Kald-økonomi: track-data
  // hentes kun for koder m. ændret scan-total (free-plan ~100 kald/md).
  makeSource({
    id: 'qrtiger',
    label: 'QR TIGER (QR-koder + scans)',
    syncEndpoint: '/api/integrations/qrtiger/sync',
    statusEndpoint: '/api/integrations/qrtiger/summary',
    progressProvider: 'qrtiger',
  }),
  // Cloudflare ([D-165]): daglig CDN-trafik pr. zone — 3-d overlap på
  // vandmærket, 365 dages backfill ved første kørsel.
  makeSource({
    id: 'cloudflare',
    label: 'Cloudflare (CDN-trafik)',
    syncEndpoint: '/api/integrations/cloudflare/sync',
    statusEndpoint: '/api/integrations/cloudflare/summary',
    progressProvider: 'cloudflare',
  }),
  // Stape ([D-181]): server-side events pr. tag-klient (consent-split m.m.) —
  // 3-d overlap på vandmærket, backfill fra analytics-startdatoen (okt. 2024).
  makeSource({
    id: 'stape',
    label: 'Stape (server-side events)',
    syncEndpoint: '/api/integrations/stape/sync',
    statusEndpoint: '/api/integrations/stape/summary',
    progressProvider: 'stape',
  }),
  // Loyalty Key ([D-349]): hele transaktionslisten hentes hver gang (få kald),
  // derefter kontrollen af butikshandler.
  makeSource({
    id: 'loyaltykey',
    label: 'Loyalty Key (cashback + kontrol)',
    syncEndpoint: '/api/integrations/loyaltykey/sync',
    statusEndpoint: '/api/integrations/loyaltykey/summary',
    progressProvider: 'loyaltykey',
  }),
  // Relewise ([D-368]): webshoppens egen søgning (ranglisten, 1 kald) → spejl
  // + historik + nye søgeord i SEO-universet. Aldrig et søge-kald.
  makeSource({
    id: 'relewise',
    label: 'Relewise (webshop-søgninger)',
    syncEndpoint: '/api/integrations/relewise/sync',
    statusEndpoint: '/api/integrations/relewise/summary',
    progressProvider: 'relewise',
  }),
  // DataForSEO ([D-208]): dagens placeringer + konkurrent-liste. Kilden koster
  // penge pr. kald, saa den springer selv over naar dagens billede er hentet.
  makeSource({
    id: 'dataforseo',
    label: 'DataForSEO (placeringer + konkurrenter)',
    syncEndpoint: '/api/integrations/dataforseo/sync',
    statusEndpoint: '/api/integrations/dataforseo/summary',
    progressProvider: 'dataforseo',
  }),
  // SEO-indholds-spejlet ([D-226]): webshoppens tekstindhold (produkter,
  // varekategorier, sider, indlæg) → seo_content_items. Kun læsning mod sitet.
  makeSource({
    id: 'seo_content',
    label: 'Webshop-indhold (SEO-spejl)',
    syncEndpoint: '/api/integrations/wordpress/sync?service=seo_content',
    statusEndpoint: '/api/integrations/wordpress/seo-content-summary',
    // Deler wordpress-progress-nøglen med Woo/FluentForms (kaskaden kører
    // sekventielt, og provider-mutex'en forhindrer overlap).
    progressProvider: 'wordpress',
  }),
]);

/** Næste planlagte auto-kørsel pr. kilde ([D-155]) — {jobName, nextRunAt}. */
const nextRuns = ref<Record<string, { jobName: string; nextRunAt: number | null }>>({});

async function fetchNextRuns(): Promise<void> {
  try {
    const res = await fetch('/api/integrations/next-runs');
    const data = (await res.json()) as { bySource?: Record<string, { jobName: string; nextRunAt: number | null }> };
    nextRuns.value = data.bySource ?? {};
  } catch {
    nextRuns.value = {};
  }
}

/** "Næste kørsel"-label: lokal tid (Europe/Copenhagen) + i nat/i morgen. */
function nextRunLabel(sourceId: string): string {
  const at = nextRuns.value[sourceId]?.nextRunAt;
  if (!at) return '—';
  const d = new Date(at);
  const time = formatTimeDa(d);
  const today = new Date().toDateString();
  return d.toDateString() === today ? `I dag ${time}` : `I nat ${time}`;
}

function nextRunTip(sourceId: string): string {
  const r = nextRuns.value[sourceId];
  if (!r?.nextRunAt) return 'Ingen planlagt auto-kørsel kendt for denne kilde.';
  const d = new Date(r.nextRunAt);
  return `Næste automatiske kørsel: ${formatEpochDateTimeDa(d.getTime(), { relative: false, seconds: false })} (natjobbet ${r.jobName}).`;
}

/** Afsluttende budget-recompute kører ([D-083]) efter alle kilder er færdige. */
const recomputing = ref(false);
/** Fejl-besked hvis recompute-trinnet fejler (ellers null). */
const recomputeError = ref<string | null>(null);

const loadingStatus = ref(false);
/** Ticking timestamp — opdateres hver sekund så elapsed/ago-displays er live. */
const now = ref(Date.now());
let tickerInterval: ReturnType<typeof setInterval> | null = null;

/** Polling-handles pr. provider — så vi kan stoppe dem når sync er færdig. */
const pollHandles = new Map<string, ReturnType<typeof setInterval>>();
/** Poller for den server-side kaskade-status (sync-all/status). */
let cascadeStatusHandle: ReturnType<typeof setInterval> | null = null;

/** Kaskaden er aktiv (kører eller har kilder i kø, eller genberegner). */
const cascadeActive = computed<boolean>(
  () =>
    recomputing.value ||
    sources.value.some((s) => s.status === 'running' || s.status === 'queued'),
);

/** Alle valgte (+ konfigurerede) sources er færdige (success eller error). */
const allSelectedComplete = computed<boolean>(() => {
  const active = sources.value.filter((s) => s.selected && s.configured);
  if (active.length === 0) return false;
  return active.every((s) => s.status === 'success' || s.status === 'error');
});

const successCount = computed<number>(
  () => sources.value.filter((s) => s.status === 'success').length,
);

const errorCount = computed<number>(
  () => sources.value.filter((s) => s.status === 'error').length,
);

const syncableCount = computed<number>(
  () => sources.value.filter((s) => s.selected && s.configured).length,
);

const masterCheckState = computed<'checked' | 'unchecked' | 'indeterminate'>(() => {
  const configurable = sources.value.filter((s) => s.configured);
  if (configurable.length === 0) return 'unchecked';
  const selectedCount = configurable.filter((s) => s.selected).length;
  if (selectedCount === 0) return 'unchecked';
  if (selectedCount === configurable.length) return 'checked';
  return 'indeterminate';
});

function toggleMaster(): void {
  const shouldSelectAll = masterCheckState.value !== 'checked';
  for (const s of sources.value) {
    if (s.configured) s.selected = shouldSelectAll;
  }
}

// ---------------------------------------------------------------------------
// Status fetch (last sync + this run preview)
// ---------------------------------------------------------------------------

/** ISO-dato (YYYY-MM-DD) for i går i UTC. */
function yesterdayIso(): string {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Beregn "Denne kørsel"-preview ud fra seneste data-dato. Vinduet er
 * (seneste + 1 dag) → i går. Hvis allerede ajourført, vis "Ajourført";
 * hvis ingen data endnu, vis fuld-backfill-tekst.
 */
function previewFromLatest(latestIso: string | null): { preview: string; lastSyncAt: string | null; day: string | null } {
  const yIso = yesterdayIso();
  if (!latestIso) {
    return {
      preview: `${formatDateRangeDa('2025-01-01', yIso)} (fuld backfill)`,
      lastSyncAt: null,
      day: null,
    };
  }
  const nextStart = new Date(`${latestIso}T00:00:00Z`);
  nextStart.setUTCDate(nextStart.getUTCDate() + 1);
  const nextStartIso = nextStart.toISOString().slice(0, 10);
  return {
    preview: nextStartIso <= yIso ? formatDateRangeDa(nextStartIso, yIso) : 'Ajourført',
    lastSyncAt: latestIso,
    // Kun en 1-dags-periode vises relativt ("I går").
    day: nextStartIso === yIso ? yIso : null,
  };
}

/**
 * Hent status pr. kilde. Hver kildes summary-endpoint har sin egen shape, så
 * vi parser pr. id og normaliserer til { configured, lastSyncAt, preview }.
 */
async function fetchAllStatus(): Promise<void> {
  loadingStatus.value = true;
  await Promise.all(
    sources.value.map(async (s) => {
      if (!s.statusEndpoint) return;
      s.thisRunPreviewDay = null;
      try {
        const res = await fetch(s.statusEndpoint);
        const data = (await res.json()) as Record<string, unknown>;
        if (!res.ok) {
          s.configured = false;
          s.thisRunPreview = 'Endpoint-fejl';
          return;
        }

        if (s.id === 'ga4') {
          // GA4-summary har ingen ok/configured-felter — udled fra properties.
          const totals = (data.totals ?? {}) as { properties?: number };
          const props = (data.properties ?? []) as Array<{ lastDate?: string | null }>;
          s.configured = (totals.properties ?? 0) > 0;
          if (!s.configured) {
            s.thisRunPreview = 'Ikke konfigureret';
            s.selected = false;
            return;
          }
          const latest = props
            .map((p) => p.lastDate ?? null)
            .filter((d): d is string => !!d)
            .sort()
            .at(-1) ?? null;
          const { preview, lastSyncAt, day } = previewFromLatest(latest);
          s.lastSyncAt = lastSyncAt;
          s.thisRunPreview = preview;
          s.thisRunPreviewDay = day;
          return;
        }

        // Ad-platform-summaries (Google/Bing/Meta) deler shape: { accounts[],
        // totals } uden ok/configured — udled fra accounts + insightsLastDate.
        if (s.id === 'google_ads' || s.id === 'bing_ads' || s.id === 'meta_ads') {
          const totals = (data.totals ?? {}) as { accounts?: number };
          const accounts = (data.accounts ?? []) as Array<{ insightsLastDate?: string | null }>;
          s.configured = (totals.accounts ?? 0) > 0;
          if (!s.configured) {
            s.thisRunPreview = 'Ikke konfigureret';
            s.selected = false;
            return;
          }
          const latest = accounts
            .map((a) => a.insightsLastDate ?? null)
            .filter((d): d is string => !!d)
            .sort()
            .at(-1) ?? null;
          const { preview, lastSyncAt, day } = previewFromLatest(latest);
          s.lastSyncAt = lastSyncAt;
          s.thisRunPreview = preview;
          s.thisRunPreviewDay = day;
          return;
        }

        // GSC-summary: { sites[], totals } — udled fra sites + lastDate.
        // (GSC-syncen genhenter selv 3 buffer-dage for delayed data.)
        if (s.id === 'gsc') {
          const totals = (data.totals ?? {}) as { sites?: number };
          const sitesArr = (data.sites ?? []) as Array<{ lastDate?: string | null }>;
          s.configured = (totals.sites ?? 0) > 0;
          if (!s.configured) {
            s.thisRunPreview = 'Ikke konfigureret';
            s.selected = false;
            return;
          }
          const latest = sitesArr
            .map((x) => x.lastDate ?? null)
            .filter((d): d is string => !!d)
            .sort()
            .at(-1) ?? null;
          const { preview, lastSyncAt, day } = previewFromLatest(latest);
          s.lastSyncAt = lastSyncAt;
          s.thisRunPreview = preview;
          s.thisRunPreviewDay = day;
          return;
        }

        // BC / WooCommerce / FX deler { ok, configured, <latest-felt> }.
        if (data.ok === false) {
          s.configured = false;
          s.thisRunPreview = 'Endpoint-fejl';
          return;
        }
        s.configured = Boolean(data.configured);
        if (!s.configured) {
          s.thisRunPreview = 'Ikke konfigureret';
          s.selected = false;
          return;
        }

        if (s.id === 'fx') {
          // FX er månedlig (keyless): latestMonth = 'YYYY-MM'. Vi henter altid
          // alle måneder fra 2025 (idempotent upsert) → simpel preview.
          const latestMonth = (data.latestMonth as string | null) ?? null;
          s.lastSyncAt = latestMonth;
          s.thisRunPreview = latestMonth
            ? `Månedskurser t.o.m. ${formatMonthDa(latestMonth)} → genhentes`
            : 'Alle måneder (fuld backfill)';
          return;
        }

        if (s.id === 'klaviyo') {
          // Klaviyo er full upsert (ingen dato-watermark): preview = tællinger.
          const counts = (data.counts ?? {}) as {
            profiles?: number;
            campaigns?: number;
            flows?: number;
          };
          s.lastSyncAt = (data.lastSyncDate as string | null) ?? null;
          s.thisRunPreview = `Fuld opdatering (${(counts.profiles ?? 0).toLocaleString('da-DK')} profiler · ${counts.campaigns ?? 0} kampagner · ${counts.flows ?? 0} flows)`;
          return;
        }

        if (s.id === 'calendly') {
          // Calendly er full upsert (lille volumen): preview = tællinger.
          const counts = (data.counts ?? {}) as { bookings?: number; matched?: number };
          s.lastSyncAt = (data.lastSyncDate as string | null) ?? null;
          s.thisRunPreview = `Fuld opdatering (${counts.bookings ?? 0} bookinger · ${counts.matched ?? 0} BC-matchede)`;
          return;
        }

        if (s.id === 'typeform') {
          // Typeform: watermark pr. form (submitted_at) + fuld upsert af nye.
          const counts = (data.counts ?? {}) as { forms?: number; responses?: number };
          s.lastSyncAt = (data.lastSyncDate as string | null) ?? null;
          s.thisRunPreview = `${counts.forms ?? 0} forms (${counts.responses ?? 0} svar) → nye svar hentes`;
          return;
        }

        if (s.id === 'fluentforms') {
          // FluentForms ([D-155]): rækker + seneste svar-dato; nye svar hentes.
          s.lastSyncAt = (data.latestDate as string | null) ?? null;
          s.thisRunPreview = `${(Number(data.rows) || 0).toLocaleString('da-DK')} svar i alt → nye hentes`;
          return;
        }

        if (s.id === 'bc_customers') {
          // BC kunder ([D-155]): full upsert — preview = tællinger.
          const at = data.lastSyncedAt as number | null;
          s.lastSyncAt = at ? new Date(at).toISOString().slice(0, 10) : null;
          s.thisRunPreview = `Fuld opdatering (${(Number(data.customers) || 0).toLocaleString('da-DK')} kunder · ${(Number(data.withDiscGroup) || 0).toLocaleString('da-DK')} m. rabatgruppe)`;
          return;
        }

        if (s.id === 'samesystem') {
          // SameSystem ([D-153]): 35-dages-inkrement (retro-justeringer).
          s.lastSyncAt = (data.latestDate as string | null) ?? null;
          s.thisRunPreview = `Seneste 35 dage (${(Number(data.shifts) || 0).toLocaleString('da-DK')} vagter i spejlet)`;
          return;
        }

        if (s.id === 'cloudflare') {
          // Cloudflare ([D-165]): dags-rækker pr. zone; kun 3-d overlap
          // genhentes efter første backfill (365 d).
          const counts = (data.counts ?? {}) as { zones?: number; zoneDays?: number; latestDate?: string | null };
          s.lastSyncAt = (counts.latestDate as string | null) ?? (data.lastSyncDate as string | null) ?? null;
          s.thisRunPreview = `${(counts.zoneDays ?? 0).toLocaleString('da-DK')} dags-rækker (${counts.zones ?? 0} zoner) → kun seneste dage genhentes`;
          return;
        }

        if (s.id === 'qrtiger') {
          // QR TIGER ([D-161]): kode-listen + KUN ændrede koders scan-historik
          // (free-planens kald-loft ~100/md).
          const counts = (data.counts ?? {}) as { codes?: number; scanDays?: number };
          s.lastSyncAt = (data.lastSyncDate as string | null) ?? null;
          s.thisRunPreview = `${counts.codes ?? 0} koder (${counts.scanDays ?? 0} scan-dage) → kun ændrede koder hentes`;
          return;
        }

        if (s.id === 'dataforseo') {
          // [D-208]: søgeord i seneste øjebliksbillede + saldoen, så forbruget
          // på den forudbetalte konto kan ses uden at logge ind hos udbyderen.
          const counts = (data.counts ?? {}) as { keywords?: number; competitors?: number; snapshots?: number; latestDate?: string | null };
          s.lastSyncAt = (counts.latestDate as string | null) ?? (data.lastSyncDate as string | null) ?? null;
          const balance = (data.balanceLabel as string | null) ?? null;
          s.thisRunPreview =
            `${(counts.keywords ?? 0).toLocaleString('da-DK')} placeringer i ${counts.snapshots ?? 0} øjebliksbilleder` +
            ` · ${counts.competitors ?? 0} konkurrenter` +
            (balance ? ` · ${balance}` : '') +
            ' → hentes én gang i døgnet';
          return;
        }

        if (s.id === 'seo_content') {
          // SEO-indholds-spejlet ([D-226]): elementer pr. type + seneste sync.
          const ts = data.lastSyncedAt == null ? null : Number(data.lastSyncedAt);
          s.lastSyncAt = ts ? new Date(ts).toISOString().slice(0, 10) : null;
          s.thisRunPreview =
            `${(Number(data.products) || 0).toLocaleString('da-DK')} produkter · ` +
            `${Number(data.categories) || 0} kategorier · ` +
            `${Number(data.pages) || 0} sider/indlæg i spejlet`;
          return;
        }

        if (s.id === 'loyaltykey') {
          // Loyalty Key ([D-349]): hele listen genhentes, kontrollen kører bagefter.
          const counts = (data.counts ?? {}) as { transactions?: number; openFlags?: number };
          s.lastSyncAt = (data.lastSyncDate as string | null) ?? null;
          s.thisRunPreview = `${(counts.transactions ?? 0).toLocaleString('da-DK')} transaktioner → hele listen genhentes og kontrolleres${counts.openFlags ? ` (${counts.openFlags} åbne flag)` : ''}`;
          return;
        }

        if (s.id === 'stape') {
          // Stape ([D-181]): dags-rækker pr. klient × event; 3-d overlap efter
          // første backfill (fra analytics-startdatoen).
          const counts = (data.counts ?? {}) as { containers?: number; eventDays?: number; latestDate?: string | null };
          s.lastSyncAt = (counts.latestDate as string | null) ?? (data.lastSyncDate as string | null) ?? null;
          s.thisRunPreview = `${(counts.eventDays ?? 0).toLocaleString('da-DK')} event-rækker (${counts.containers ?? 0} container${(counts.containers ?? 0) === 1 ? '' : 'e'}) → kun seneste dage genhentes`;
          return;
        }

        // BC / WooCommerce / Meraki / GSC-queries deler { ok, configured,
        // <latest-felt> }-kontrakten.
        const latestIso =
          s.id === 'business_central_production'
            ? ((data.latestPostingDate as string | null) ?? null)
            : ((data.latestDate as string | null) ?? null); // woocommerce/meraki/gsc_queries
        const { preview, lastSyncAt, day } = previewFromLatest(latestIso);
        s.lastSyncAt = lastSyncAt;
        s.thisRunPreview = preview;
        s.thisRunPreviewDay = day;
      } catch {
        s.thisRunPreview = 'Status-fejl';
      }
    }),
  );
  loadingStatus.value = false;
}

// ---------------------------------------------------------------------------
// Polling
// ---------------------------------------------------------------------------

function startPolling(source: SyncSource): void {
  // Stop evt. eksisterende poller for denne kilde.
  stopPolling(source.progressProvider);

  // Google sub-services (google_ads/google_ga4/google_gsc/google_gsc_queries)
  // er IKKE provider-IDs ([D-039]) — /:provider/sync-progress afviser dem med
  // Zod-fejl. De poller i stedet sub-service-progress-routen (session 31-fix;
  // GA4 viste før kun spinner fordi polling-fejlen blev slugt).
  const progressUrl = source.progressProvider.startsWith('google_')
    ? `/api/integrations/google/sub-service-progress?service=${source.progressProvider.slice('google_'.length)}`
    : `/api/integrations/${source.progressProvider}/sync-progress`;

  const tick = async (): Promise<void> => {
    try {
      const res = await fetch(progressUrl);
      // Endpointet returnerer SyncProgress-fields DIREKTE (ikke wrapped i
      // { ok, progress }). Hvis isRunning-feltet er til stede er det en
      // valid progress-snapshot vi kan render'e.
      const data = (await res.json()) as SyncProgressShape;
      if (data && typeof data.isRunning === 'boolean') {
        source.progress = data;
      }
    } catch {
      // Polling-fejl er ikke kritiske — sync-respons fra POST giver os det
      // endelige resultat. Vi fortsætter med at polle.
    }
  };

  // Første tick straks så UI ikke skal vente 500ms før første progress vises.
  void tick();
  const handle = setInterval(tick, 500);
  pollHandles.set(source.progressProvider, handle);
}

function stopPolling(progressProvider: string): void {
  const handle = pollHandles.get(progressProvider);
  if (handle) {
    clearInterval(handle);
    pollHandles.delete(progressProvider);
  }
}

function stopAllPolling(): void {
  for (const handle of pollHandles.values()) {
    clearInterval(handle);
  }
  pollHandles.clear();
}

// ---------------------------------------------------------------------------
// Sync trigger
// ---------------------------------------------------------------------------

/**
 * Trigger sync. Session 53: kaskaden kører nu SERVER-SIDE i baggrunden — vi
 * affyrer ét kald til /api/integrations/sync-all og poller /sync-all/status.
 * Det betyder at vinduet kan lukkes (serveren fortsætter), at de fire Google-
 * under-services ikke længere kolliderer (server kører dem sekventielt), og at
 * den afsluttende budget-genberegning også kører server-side.
 */
async function runSync(): Promise<void> {
  recomputeError.value = null;
  const selectedIds = sources.value
    .filter((s) => s.selected && s.configured)
    .map((s) => s.id);
  if (selectedIds.length === 0) return;

  // Optimistisk: valgte kilder → 'i kø' (serveren kører dem én ad gangen).
  for (const s of sources.value) {
    if (s.selected && s.configured) {
      s.status = 'queued';
      s.resultText = null;
      s.progress = null;
      s.startedAt = null;
      s.finishedAt = null;
    }
  }

  try {
    const res = await fetch('/api/integrations/sync-all', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ sources: selectedIds }),
    });
    const data = (await res.json()) as { ok: boolean; error?: string };
    if (!res.ok || !data.ok) {
      const msg = cleanErrorText(data.error ?? `HTTP ${res.status}`);
      for (const s of sources.value) {
        if (s.status === 'queued') {
          s.status = 'error';
          s.resultText = msg;
        }
      }
      return;
    }
  } catch (err) {
    const msg = cleanErrorText(err instanceof Error ? err.message : 'Kunne ikke starte sync');
    for (const s of sources.value) {
      if (s.status === 'queued') {
        s.status = 'error';
        s.resultText = msg;
      }
    }
    return;
  }

  // Kaskaden kører nu på serveren — følg dens status (overlever vindue-luk).
  startCascadeStatusPolling();
}

// ---------------------------------------------------------------------------
// Server-side kaskade-status (sync-all/status)
// ---------------------------------------------------------------------------

/** Map server-kaskade-state → per-kilde UI-rækker + recompute-banner. */
function applyCascadeState(cs: CascadeStateShape): void {
  for (const step of cs.steps ?? []) {
    const s = sources.value.find((x) => x.id === step.id);
    if (!s) continue;
    if (step.status === 'pending') {
      if (s.status !== 'success' && s.status !== 'error') s.status = 'queued';
    } else if (step.status === 'running') {
      s.status = 'running';
      if (!s.startedAt) s.startedAt = step.startedAt ?? Date.now();
      if (!pollHandles.has(s.progressProvider)) startPolling(s);
    } else if (step.status === 'success') {
      s.status = 'success';
      s.resultText = step.warnings.length > 0 ? `OK (${step.warnings.length} advarsler)` : 'OK';
      if (!s.finishedAt) s.finishedAt = step.finishedAt ?? Date.now();
      stopPolling(s.progressProvider);
    } else if (step.status === 'error') {
      s.status = 'error';
      s.resultText = cleanErrorText(step.error ?? 'Fejl');
      if (!s.finishedAt) s.finishedAt = step.finishedAt ?? Date.now();
      stopPolling(s.progressProvider);
    }
  }
  recomputing.value = cs.recompute?.status === 'running';
  recomputeError.value =
    cs.recompute?.status === 'error' ? (cs.recompute.error ?? 'Recompute fejlede') : null;
}

async function pollCascadeStatusTick(): Promise<void> {
  try {
    const res = await fetch('/api/integrations/sync-all/status');
    const cs = (await res.json()) as CascadeStateShape;
    applyCascadeState(cs);
    if (!cs.running) {
      stopCascadeStatusPolling();
      stopAllPolling();
      // Friske lastSync/preview-værdier til næste kørsel.
      await fetchAllStatus();
    }
  } catch {
    // Polling-fejl er ikke kritiske — prøver igen næste tick.
  }
}

function startCascadeStatusPolling(): void {
  stopCascadeStatusPolling();
  void pollCascadeStatusTick();
  cascadeStatusHandle = setInterval(() => void pollCascadeStatusTick(), 1000);
}

function stopCascadeStatusPolling(): void {
  if (cascadeStatusHandle) {
    clearInterval(cascadeStatusHandle);
    cascadeStatusHandle = null;
  }
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/**
 * Renser en fejl-besked til UI-visning: striber HTML-tags (fx en WooCommerce
 * 504-gateway-side der returneres som en hel HTML-fejlside) + kollapser
 * whitespace + trunkerer, så én ulækker fejl ikke vælter rækken.
 */
function cleanErrorText(raw: string): string {
  const stripped = raw
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (stripped.length === 0) return 'Ukendt fejl';
  return stripped.length > 160 ? `${stripped.slice(0, 157)}…` : stripped;
}

/**
 * "Sidst synket"-label pr. kilde via den kanoniske dato-konvention
 * (`web/src/lib/datetime.ts`). FX er måneds-granulær (YYYY-MM); øvrige er
 * dato-granulære (YYYY-MM-DD med I dag/I går-genvej).
 */
function lastSyncLabel(s: SyncSource): string {
  if (!s.lastSyncAt) return 'Aldrig';
  return s.id === 'fx' ? formatMonthDa(s.lastSyncAt) : formatDateDa(s.lastSyncAt);
}

/** Relativ "tid siden" via den kanoniske tidsinterval-konvention (§11.1). */
function formatRelativeAgo(timestamp: number): string {
  // atMs med: i en udskrift skrives den konkrete dato i stedet (§27).
  return formatRelativeAgoDa(now.value - timestamp, timestamp);
}

/**
 * Returnerer elapsed-ms for en kilde. Fryser ved finishedAt når status ikke
 * længere er 'running' — så badge'n ikke fortsætter med at tikke up efter
 * sync er færdig. Falder tilbage til 0 hvis startedAt mangler.
 */
function elapsedMsFor(s: SyncSource): number {
  if (!s.startedAt) return 0;
  const endTime = s.status === 'running' ? now.value : (s.finishedAt ?? now.value);
  return Math.max(0, endTime - s.startedAt);
}

/** Display-label for current sync phase (BC's "general-ledger-entries" → "GL-entries"). */
function phaseLabel(phase: string): string {
  if (phase === 'general-ledger-entries') return 'Henter posteringer';
  if (phase === 'starter') return 'Starter';
  // Generic fallback: replace dashes with spaces, capitalize.
  const cleaned = phase.replace(/-/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      void openAndRestore();
    } else {
      // Vindue lukket: stop AL UI-polling. Server-side kaskade fortsætter.
      stopAllPolling();
      stopCascadeStatusPolling();
    }
  },
);

/**
 * Ved åbning: nulstil til idle, hent status, og GENOPTAG visningen hvis en
 * server-side kaskade allerede kører (startet før vinduet blev lukket). Det er
 * kernen i baggrunds-syncen: man kan lukke og åbne frit uden at miste status.
 */
async function openAndRestore(): Promise<void> {
  recomputing.value = false;
  recomputeError.value = null;
  for (const s of sources.value) {
    if (s.status !== 'running') {
      s.status = 'idle';
      s.resultText = null;
      s.progress = null;
      s.startedAt = null;
      s.finishedAt = null;
      if (s.configured) s.selected = true;
    }
  }
  await Promise.all([fetchAllStatus(), fetchNextRuns()]);
  try {
    const res = await fetch('/api/integrations/sync-all/status');
    const cs = (await res.json()) as CascadeStateShape;
    const active =
      cs.running ||
      (cs.steps ?? []).some((st) => st.status === 'running' || st.status === 'pending');
    if (active) {
      applyCascadeState(cs);
      startCascadeStatusPolling();
    }
  } catch {
    // Ignore — viser bare idle-tilstanden.
  }
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.open) {
    emit('close');
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
  // Live ticker for elapsed-time-displays.
  tickerInterval = setInterval(() => {
    now.value = Date.now();
  }, 1000);
  if (props.open) void openAndRestore();
});

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown);
  if (tickerInterval) clearInterval(tickerInterval);
  stopAllPolling();
  stopCascadeStatusPolling();
});

function onBackdropClick(): void {
  emit('close');
}

function onModalClick(event: MouseEvent): void {
  event.stopPropagation();
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="props.open"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sync-cascade-title"
        @click="onBackdropClick"
      >
        <div class="absolute inset-0 bg-ink/40"></div>

        <!-- Sticky header/footer ([D-155]): dialogen er en flex-kolonne m.
             max-højde; KUN kildelisten scroller (scrollbar-none som Budget/
             Salgsstatistik — scroll virker fortsat), header + footer står fast. -->
        <div
          class="relative w-full max-w-3xl max-h-[85vh] flex flex-col rounded-xl bg-surface border border-line shadow-xl"
          @click="onModalClick"
        >
          <header class="shrink-0 px-6 pt-6 pb-4">
            <h2 id="sync-cascade-title" class="text-xl font-medium text-ink tracking-tight">
              Manuel sync
            </h2>
            <p class="mt-1 text-sm text-muted max-w-xl">
              Hent friske data fra hver kilde frem til og med i går. Alle kilder er
              valgt som standard, fjern flueben for dem du vil springe over.
            </p>
          </header>

          <div class="px-6 flex-1 min-h-0 overflow-y-auto scrollbar-none">
            <table class="w-full text-sm border-collapse">
              <!-- Kolonneoverskrifterne er sticky INDE i scroll-området, så de
                   står fast mens kilderne ruller under dem. -->
              <thead>
                <tr>
                  <th class="sticky top-0 z-10 bg-surface w-10 py-2.5 text-left border-y border-line">
                    <input
                      type="checkbox"
                      :checked="masterCheckState === 'checked'"
                      :indeterminate.prop="masterCheckState === 'indeterminate'"
                      class="rounded border-line"
                      aria-label="Vælg alle kilder"
                      :disabled="cascadeActive"
                      @change="toggleMaster"
                    />
                  </th>
                  <th class="sticky top-0 z-10 bg-surface py-2.5 text-left text-[11px] text-faint font-medium border-y border-line">
                    Kilde
                  </th>
                  <th class="sticky top-0 z-10 bg-surface py-2.5 text-left text-[11px] text-faint font-medium border-y border-line">
                    Sidst synket
                  </th>
                  <th class="sticky top-0 z-10 bg-surface py-2.5 text-left text-[11px] text-faint font-medium border-y border-line">
                    Denne kørsel
                  </th>
                  <th
                    class="sticky top-0 z-10 bg-surface py-2.5 text-left text-[11px] text-faint font-medium border-y border-line whitespace-nowrap"
                    v-tip="'Hvornår kilden næste gang synkroniseres automatisk (natjobbet).'"
                  >
                    Næste kørsel
                  </th>
                  <th class="sticky top-0 z-10 bg-surface w-10 py-2.5 border-y border-line"></th>
                </tr>
              </thead>
              <tbody>
                <template v-for="s in sources" :key="s.id">
                  <tr
                    class="border-b border-line/60"
                    :class="!s.configured ? 'opacity-50' : ''"
                  >
                    <td class="py-3 align-top">
                      <input
                        v-model="s.selected"
                        type="checkbox"
                        class="rounded border-line"
                        :disabled="!s.configured || s.status === 'running' || s.status === 'queued'"
                        :aria-label="`Vælg ${s.label}`"
                      />
                    </td>
                    <td class="py-3 align-top">
                      <div class="flex flex-col">
                        <span class="text-ink font-medium">{{ s.label }}</span>
                        <!-- Records-count + elapsed-tid badge under label når sync kører/færdig -->
                        <span
                          v-if="s.progress && (s.status === 'running' || s.status === 'success')"
                          class="text-[11px] text-faint tnum mt-0.5"
                        >
                          {{ s.progress.current.toLocaleString('da-DK') }} records ·
                          <span v-duration="elapsedMsFor(s)">{{ formatDurationDa(elapsedMsFor(s)) }}</span>
                        </span>
                        <span
                          v-else-if="s.status === 'error' && s.resultText"
                          class="text-[11px] text-red mt-0.5"
                        >
                          {{ s.resultText }}
                        </span>
                      </div>
                    </td>
                    <td class="py-3 align-top text-muted tnum" v-fulldate="s.id === 'fx' ? null : s.lastSyncAt">
                      {{ lastSyncLabel(s) }}
                    </td>
                    <td class="py-3 align-top text-muted tnum" v-fulldate="s.thisRunPreviewDay">
                      {{ s.thisRunPreview }}
                    </td>
                    <td class="py-3 align-top text-muted tnum whitespace-nowrap" v-tip="nextRunTip(s.id)" v-fulldate="nextRuns[s.id]?.nextRunAt">
                      {{ nextRunLabel(s.id) }}
                    </td>
                    <td class="py-3 align-top">
                      <span
                        v-if="s.status === 'queued'"
                        class="text-faint text-[11px] italic"
                        :title="'Venter i kø'"
                      >
                        i kø
                      </span>
                      <span
                        v-else-if="s.status === 'running'"
                        class="inline-block w-3.5 h-3.5 rounded-full border border-line border-t-active animate-spin"
                        :title="'Synker…'"
                      ></span>
                      <span
                        v-else-if="s.status === 'success'"
                        class="text-green text-base"
                        :title="s.resultText ?? 'OK'"
                      >
                        ✓
                      </span>
                      <span
                        v-else-if="s.status === 'error'"
                        class="text-red text-base"
                        :title="s.resultText ?? 'Fejl'"
                      >
                        ✕
                      </span>
                    </td>
                  </tr>
                  <!-- Sub-row med phase + processed + elapsed + relative-ago (kun under running) -->
                  <tr
                    v-if="s.status === 'running' && s.progress"
                    class="border-b border-line/60 bg-subtle/40"
                  >
                    <td></td>
                    <td class="py-2 text-[12px] italic text-muted">
                      {{ phaseLabel(s.progress.phase) }}:
                      {{ s.progress.current.toLocaleString('da-DK') }}
                      <template v-if="s.progress.total"
                        >/{{ s.progress.total.toLocaleString('da-DK') }}</template
                      >
                      behandlet
                    </td>
                    <td class="py-2 text-[12px] text-muted tnum">
                      <strong v-if="s.progress.current > 0">{{ s.progress.current.toLocaleString('da-DK') }} records</strong>
                    </td>
                    <td class="py-2 text-[12px] italic text-faint tnum" v-duration="now - (s.startedAt ?? now)">
                      elapsed {{ formatDurationDa(now - (s.startedAt ?? now)) }}
                    </td>
                    <td class="py-2 text-[12px] italic text-faint tnum"></td>
                    <td class="py-2 text-[11px] italic text-faint tnum text-right pr-3" v-fulldate="s.progress.updatedAt">
                      {{ formatRelativeAgo(s.progress.updatedAt) }}
                    </td>
                  </tr>
                </template>
                <tr v-if="loadingStatus && sources.length === 0">
                  <td colspan="6" class="py-6 text-center text-muted text-sm">
                    Indlæser kilder…
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Footer state-machine:
               1. Idle (ingen sync kørt) → Annullér + Synk N kilder
               2. Running → italic "Synker… luk ikke vinduet" + (knapper skjult/disabled)
               3. All complete → success/error-banner + Luk-knap -->
          <footer class="shrink-0 px-6 py-4 border-t border-line">
            <!-- State 2.5: alle kilder færdige, budget genberegnes nu -->
            <template v-if="recomputing">
              <div class="flex items-center justify-end gap-2">
                <span
                  class="inline-block w-3.5 h-3.5 rounded-full border border-line border-t-active animate-spin"
                ></span>
                <p class="text-sm italic text-faint">Genberegner budget…</p>
              </div>
            </template>

            <!-- State 3: alle færdige → success/error-banner + Luk -->
            <template v-else-if="allSelectedComplete">
              <div
                class="mb-3 px-4 py-3 rounded-lg text-sm"
                :class="
                  errorCount > 0
                    ? 'bg-red/10 text-red border border-red/30'
                    : 'bg-green/10 text-green border border-green/30'
                "
              >
                <template v-if="errorCount === 0">
                  Alle {{ successCount }}
                  {{ successCount === 1 ? 'kilde synkroniseret' : 'kilder synkroniseret' }}
                  succesfuldt. Luk dialogen for at genindlæse dashboardet.
                </template>
                <template v-else>
                  {{ successCount }} af {{ successCount + errorCount }} kilder synkroniseret;
                  {{ errorCount }} {{ errorCount === 1 ? 'fejlede' : 'fejlede' }}. Se per-række
                  for detaljer.
                </template>
              </div>
              <div
                v-if="recomputeError"
                class="mb-3 px-4 py-3 rounded-lg text-sm bg-red/10 text-red border border-red/30"
              >
                Budget-genberegningen fejlede: {{ recomputeError }}. Data er hentet, men
                Real-kolonnerne er måske ikke opdateret, prøv at synke igen.
              </div>
              <div class="flex justify-end">
                <button
                  type="button"
                  class="px-3 py-1.5 rounded-lg text-xs font-medium bg-active text-active-text hover:opacity-90 transition-opacity"
                  @click="emit('close')"
                >
                  Luk
                </button>
              </div>
            </template>

            <!-- State 2: kaskaden kører server-side → vinduet MÅ lukkes -->
            <template v-else-if="cascadeActive">
              <div class="flex items-center justify-between gap-3">
                <p class="text-sm italic text-faint">
                  Kører i baggrunden, du kan roligt lukke vinduet og åbne igen for at se status.
                </p>
                <button
                  type="button"
                  class="px-3 py-1.5 rounded-lg text-xs font-medium border border-line text-muted hover:bg-hover hover:text-ink transition-colors"
                  @click="emit('close')"
                >
                  Luk
                </button>
              </div>
            </template>

            <!-- State 1: idle → vælg + sync. Knap-størrelser matcher ProviderCard's
                 canoniske px-3 py-1.5 text-xs-mønster per [§7]. -->
            <template v-else>
              <div class="flex items-center justify-end gap-3">
                <button
                  type="button"
                  class="px-3 py-1.5 rounded-lg text-xs font-medium border border-line text-muted hover:bg-hover hover:text-ink transition-colors"
                  @click="emit('close')"
                >
                  Annullér
                </button>
                <button
                  type="button"
                  class="px-3 py-1.5 rounded-lg text-xs font-medium bg-active text-active-text hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                  :disabled="syncableCount === 0"
                  @click="runSync"
                >
                  <span v-if="syncableCount === 1">Synk 1 kilde</span>
                  <span v-else>Synk {{ syncableCount }} kilder</span>
                </button>
              </div>
            </template>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
